import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { ChevronLeft } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AccountsPanel } from "@/components/admin/AccountsPanel";
import { PromoPanel } from "@/components/admin/PromoPanel";
import { AnnouncePanel } from "@/components/admin/AnnouncePanel";
import { EmailSettingsPanel } from "@/components/admin/EmailSettingsPanel";
import { RevenuePanel } from "@/components/admin/RevenuePanel";
import { FeedbackPanel } from "@/components/admin/FeedbackPanel";
import { FeaturedPanel } from "@/components/admin/FeaturedPanel";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RightsReportsPanel } from "@/components/admin/RightsReportsPanel";
import { PromotionsPanel } from "@/components/admin/PromotionsPanel";
import { NewsAdmin } from "@/components/admin/siteNews/NewsAdmin";
import { RoadmapAdmin, IntroEditor } from "@/components/admin/siteNews/RoadmapAdmin";

const FEAT_NAMES = { timelines: "Timelines & Calendars", maps: "Maps", relationships: "Relationships & Trees", languages: "Languages", commercial: "Commercial license add-on" };
const PRICE_FIELDS = [["monthly", "Pro monthly"], ["yearly", "Pro yearly"], ["commercial_monthly", "Commercial monthly"], ["commercial_yearly", "Commercial yearly"], ["lifetime", "Pro lifetime (buy once)"], ["commercial_lifetime", "Commercial lifetime (buy once)"],
  ...Object.entries(FEAT_NAMES).flatMap(([f, n]) => [[`feat_${f}_monthly`, `${n} · monthly`], [`feat_${f}_yearly`, `${n} · yearly`]])];

function PricingEditor() {
  const { data } = useQuery({ queryKey: ["admin-pricing"], queryFn: () => api.get("/admin/pricing").then((r) => r.data) });
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (data) setForm(data); }, [data]);
  const save = async () => {
    setBusy(true);
    try { const { data: d } = await api.put("/admin/pricing", Object.fromEntries(Object.entries(form).map(([k, v]) => [k, Number(v)]))); setForm(d); toast.success("Prices updated for new subscribers"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!form) return null;
  return (
    <section className="rounded-md border border-[#2D3442] bg-[#0F131A] p-6" data-testid="admin-pricing">
      <div className="lf-label">Plan pricing (USD)</div>
      <div className="mt-4 flex flex-wrap items-end gap-4">
        {PRICE_FIELDS.map(([k, l]) => (
          <label key={k} className="text-xs text-[#A8A090]">{l}
            <Input type="number" min="0.5" step="0.01" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1 w-32" data-testid={`admin-price-${k}`} />
          </label>
        ))}
        <Button onClick={save} disabled={busy} data-testid="admin-price-save">Save prices</Button>
      </div>
      <p className="mt-3 text-xs text-[#A8A090]">New prices apply to new subscribers; existing subscriptions keep their current price.</p>
    </section>
  );
}

export default function AdminPage() {
  const { user } = useAuth();
  const isAdmin = !!user?.plan?.is_admin;
  const { data: totals } = useQuery({ queryKey: ["admin-users", "total"], queryFn: () => api.get("/admin/users", { params: { per_page: 5 } }).then((r) => r.data.total), enabled: isAdmin });
  if (!isAdmin) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen lf-glow lf-grain">
      <div className="relative z-10 mx-auto max-w-6xl px-6 py-10 sm:px-10">
        <Link to="/" className="flex items-center gap-1 text-xs text-[#A8A090] hover:text-[#F3EBDD]" data-testid="admin-back-link"><ChevronLeft className="h-3.5 w-3.5" />All universes</Link>
        <div className="lf-label mb-3 mt-12">Admin</div>
        <h1 className="font-display text-4xl tracking-tight text-[#F3EBDD] sm:text-5xl">Admin portal</h1>
        {totals != null && <p className="mt-2 text-sm text-[#A8A090]" data-testid="admin-counts">{totals} accounts</p>}
        <Tabs defaultValue="accounts" className="mt-10">
          <TabsList className="h-auto max-w-full flex-wrap justify-start" data-testid="admin-tabs">
            <TabsTrigger value="accounts" data-testid="admin-tab-accounts">Accounts</TabsTrigger>
            <TabsTrigger value="revenue" data-testid="admin-tab-revenue">Revenue</TabsTrigger>
            <TabsTrigger value="billing" data-testid="admin-tab-billing">Pricing & events</TabsTrigger>
            <TabsTrigger value="email" data-testid="admin-tab-email">Email</TabsTrigger>
            <TabsTrigger value="feedback" data-testid="admin-tab-feedback">Beta feedback</TabsTrigger>
            <TabsTrigger value="featured" data-testid="admin-tab-featured">Featured stories</TabsTrigger>
            <TabsTrigger value="site-news" data-testid="admin-tab-site-news">Site news</TabsTrigger>
            <TabsTrigger value="promotions" data-testid="admin-tab-promotions">Promotions</TabsTrigger>
            <TabsTrigger value="rights" data-testid="admin-tab-rights">Rights reports</TabsTrigger>
          </TabsList>
          <TabsContent value="promotions" className="mt-6"><PromotionsPanel /></TabsContent>
          <TabsContent value="rights" className="mt-6"><RightsReportsPanel /></TabsContent>
          <TabsContent value="featured" className="mt-6"><FeaturedPanel /></TabsContent>
          <TabsContent value="site-news" className="mt-6 space-y-8"><NewsAdmin /><RoadmapAdmin /><IntroEditor /></TabsContent>
          <TabsContent value="revenue" className="mt-6"><RevenuePanel /></TabsContent>
          <TabsContent value="feedback" className="mt-6"><FeedbackPanel /></TabsContent>
          <TabsContent value="billing" className="mt-6 space-y-8"><PricingEditor /><PromoPanel /></TabsContent>
          <TabsContent value="email" className="mt-6 space-y-8"><AnnouncePanel adminEmail={user.email} /><EmailSettingsPanel /></TabsContent>
          <TabsContent value="accounts" className="mt-6"><AccountsPanel /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
